export const MAX_OPTIMISED_IMAGE_BYTES = 400 * 1024;
export const MAX_OPTIMISED_IMAGE_EDGE = 1400;

const JPEG_QUALITIES = [0.82, 0.72, 0.62, 0.52, 0.42, 0.32, 0.24, 0.18, 0.12];

const blobFromCanvas = (canvas, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
      return;
    }

    reject(new Error('Unable to optimise this image.'));
  }, 'image/jpeg', quality);
});

const loadImageFile = (file) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error(`Unable to read ${file.name} as an image.`));
  };
  image.src = objectUrl;
});

const drawImageToCanvas = (image, scale = 1) => {
  const canvas = document.createElement('canvas');
  const width = Math.max(Math.round(image.naturalWidth * scale), 1);
  const height = Math.max(Math.round(image.naturalHeight * scale), 1);
  const context = canvas.getContext('2d');

  canvas.width = width;
  canvas.height = height;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  return canvas;
};

const jpgNameForFile = (fileName = 'image') => {
  const baseName = fileName.replace(/\.[^/.]+$/, '') || 'image';
  return `${baseName}.jpg`;
};

const fileFromBlob = (blob, fileName) => new File([blob], jpgNameForFile(fileName), {
  type: 'image/jpeg',
  lastModified: Date.now(),
});

export const optimiseImageFile = async (file, maxBytes = MAX_OPTIMISED_IMAGE_BYTES) => {
  if (!file?.type?.startsWith('image/')) return file;
  if (file.type === 'image/jpeg' && file.size <= maxBytes) return file;

  const image = await loadImageFile(file);
  const longestEdge = Math.max(image.naturalWidth || 0, image.naturalHeight || 0);
  if (!longestEdge) {
    throw new Error(`Unable to read dimensions for ${file.name}.`);
  }

  let scale = Math.min(1, MAX_OPTIMISED_IMAGE_EDGE / longestEdge);
  let bestBlob = null;

  for (let resizeAttempt = 0; resizeAttempt < 30; resizeAttempt += 1) {
    const canvas = drawImageToCanvas(image, scale);

    for (const quality of JPEG_QUALITIES) {
      const blob = await blobFromCanvas(canvas, quality);
      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }

      if (blob.size <= maxBytes) {
        return fileFromBlob(blob, file.name);
      }
    }

    const shrinkRatio = Math.sqrt(maxBytes / Math.max(bestBlob?.size || 1, 1));
    scale *= Math.min(Math.max(shrinkRatio * 0.78, 0.2), 0.72);
  }

  if (bestBlob && bestBlob.size <= maxBytes) {
    return fileFromBlob(bestBlob, file.name);
  }

  throw new Error(`Unable to reduce ${file.name} below ${(maxBytes / 1024).toFixed(0)} KB.`);
};
